import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Baby, Users, Award, ChevronRight, CalendarCheck, Flower2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { JANE_BOOKING_URL } from '../data/janeServices';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_266cf7bd-85bf-4fa7-83fe-d09732430dba/artifacts/urov4aah_Screenshot%202026-02-16%20at%209.23.56%E2%80%AFPM.png";
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/1ef24032-d123-46fc-86b1-27cf9413224d/images/85423790f6bbd52368cb846e45c32fbfd70f2b2f4461b16d287d3f747881c8e6.jpeg";

const HomePage = () => {
  const services = [
    {
      id: "pelvic-health-wellness",
      icon: Heart,
      title: "Pelvic Health & Wellness",
      description: "Personalized pelvic health care for every stage of life",
      link: "/services#pelvic-health-wellness"
    },
    {
      id: "pregnancy-birth-postpartum",
      icon: Baby,
      title: "Pregnancy, Birth & Postpartum",
      description: "Individualized support through pregnancy and recovery",
      link: "/services#pregnancy-birth-postpartum"
    },
    {
      id: "childbirth-education-cpr",
      icon: Users,
      title: "Childbirth Education & CPR",
      description: "Hands-on classes for confident, prepared parents",
      link: "/services#childbirth-education-cpr"
    },
    {
      id: "lactation-consulting",
      icon: Sparkles,
      title: "Lactation Consulting",
      description: "Breastfeeding education and feeding guidance",
      link: "/services#lactation-consulting"
    },
    {
      id: "perimenopause-menopause",
      icon: Flower2,
      title: "Perimenopause & Menopause",
      description: "Education and support through midlife changes",
      link: "/services#perimenopause-menopause"
    },
    {
      id: "maternity-concierge",
      icon: Award,
      title: "Maternity Concierge",
      description: "Ongoing, individualized attention throughout your journey",
      link: "/services#maternity-concierge"
    }
  ];

  const stats = [
    { number: "25+", label: "Years Experience" },
    { number: "8+", label: "Certifications" }
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Calm coastal water at sunrise in the Florida Keys" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              {/* Main Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight text-foreground">
                Thoughtful Care.{' '}
                <span className="text-primary">Personalized for You.</span>
              </h1>

              {/* Subheading */}
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
                Your health deserves more than a one-size-fits-all approach. We take the
                time to listen, understand your concerns, and provide personalized care,
                education, and support tailored to your needs and goals at every stage of life.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={JANE_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-online-button"
                >
                  <Button 
                    className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 rounded-full text-base font-medium transition-transform hover:-translate-y-1 shadow-lg hover:shadow-xl w-full sm:w-auto"
                  >
                    <CalendarCheck className="w-5 h-5 mr-2" />
                    Book Online
                  </Button>
                </a>
                <Link to="/services">
                  <Button 
                    data-testid="hero-services-button"
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/5 px-8 py-6 rounded-full text-base font-medium w-full sm:w-auto"
                  >
                    Explore Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Logo Display */}
            <div className="hidden lg:flex justify-center items-center animate-fade-in-up animation-delay-200">
              <div className="relative">
                <div className="absolute inset-0 bg-secondary/20 rounded-full blur-3xl" />
                <img 
                  src={LOGO_URL} 
                  alt="Bayside Birth & Pelvic Health" 
                  className="relative w-80 h-auto drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div 
                key={stat.label}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-center p-8 bg-white rounded-3xl shadow-sm"
              >
                <p className="text-5xl font-medium text-primary mb-2">{stat.number}</p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <p className="text-sm tracking-widest uppercase font-semibold text-primary">
              Our Services
            </p>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground">
              How We Can Help
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Personalized support for women at every stage of life
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link 
                key={service.id}
                to={service.link}
                data-testid={`service-card-${service.id}`}
                className="group bg-white p-8 rounded-3xl border border-border/50 shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-2"
              >
                <div className="w-16 h-16 bg-secondary/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-primary/10 transition-colors">
                  <service.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-medium text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {service.description}
                </p>
                <div className="flex items-center text-primary font-medium group-hover:gap-2 transition-all">
                  Learn More
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </Link>
            ))}
          </div>

          {/* View All Services */}
          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="view-all-services-button"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 px-8 py-4 rounded-full"
              >
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section data-testid="about-preview" className="py-24 md:py-32 bg-muted">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            {/* Content */}
            <div className="space-y-6">
              <p className="text-sm tracking-widest uppercase font-semibold text-primary">
                About Me
              </p>
              <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-foreground">
                25+ Years of Caring for Women
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                My passion has always been supporting women's health and wellness at every 
                stage of life. Whether you're preparing for birth, recovering postpartum, or 
                seeking pelvic health support, I provide compassionate, personalized care 
                to help you feel confident and empowered in your body.
              </p>
              
              {/* Credentials */}
              <div className="flex flex-wrap gap-3 justify-center">
                <span className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-foreground">
                  <Award className="w-4 h-4 text-primary" />
                  Pelvic Health Specialist
                </span>
                <span className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-foreground">
                  <Award className="w-4 h-4 text-primary" />
                  Certified Lactation Consultant
                </span>
              </div>

              <Link to="/about">
                <Button 
                  data-testid="read-more-about-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-4 rounded-full mt-4"
                >
                  Read My Story
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-base md:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Book your appointment online in just a few clicks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={JANE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-book-online-button"
            >
              <Button 
                className="bg-white text-primary hover:bg-white/90 px-8 py-6 rounded-full text-base font-medium transition-transform hover:-translate-y-1 w-full sm:w-auto"
              >
                <CalendarCheck className="w-5 h-5 mr-2" />
                Book Online
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                data-testid="cta-contact-button"
                variant="outline"
                className="border-white/60 text-primary-foreground hover:bg-white/10 px-8 py-6 rounded-full text-base font-medium w-full sm:w-auto"
              >
                Contact Details
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
